import React, { useState, useRef } from 'react';
import { PlusIcon, TrashIcon, FileTextIcon, SaveIcon, MinusIcon, MaximizeIcon, MinimizeIcon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { getDefaultCurrency } from '../../utils/appSettings';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
export function InvoiceEntry({
  onSave,
  locale = 'en-US',
  defaultCurrency
}) {
  const [formData, setFormData] = useState({
    supplier: '',
    supplierID: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    country: '',
    currency: getDefaultCurrency(),
    poReference: '',
    paymentTerms: 'Net 30',
    description: '',
    notes: '',
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: ''
  });
  const [lineItems, setLineItems] = useState([{
    id: 1,
    type: 'Item',
    itemCode: '',
    description: '',
    quantity: 1,
    uom: 'EA',
    unitPrice: 0,
    taxCode: 'STD',
    glCode: '',
    total: 0
  }]);
  const [errors, setErrors] = useState({});
  const [imageViewerOpen, setImageViewerOpen] = useState(true);
  const [isDocked, setIsDocked] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(100);
  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    supplier: true,
    paymentInfo: true
  });
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Sample invoice image URL
  const invoiceImageUrl = 'https://templates.invoicehome.com/invoice-template-us-neat-750px.png';
  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'China', 'India'];
  const currencies = [{
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'JPY',
    name: 'Japanese Yen'
  }, {
    code: 'CAD',
    name: 'Canadian Dollar'
  }, {
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'INR',
    name: 'Indian Rupee'
  }];
  const uomOptions = ['EA', 'KG', 'LB', 'M', 'CM', 'L', 'ML', 'HR', 'DAY'];
  const taxCodes = ['STD', 'RED', 'ZERO', 'EXM'];
  const paymentTermsOptions = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on Receipt'];
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const addLineItem = () => {
    const newId = lineItems.length > 0 ? Math.max(...lineItems.map(item => item.id)) + 1 : 1;
    setLineItems([...lineItems, {
      id: newId,
      type: 'Item',
      itemCode: '',
      description: '',
      quantity: 1,
      uom: 'EA',
      unitPrice: 0,
      taxCode: 'STD',
      glCode: '',
      total: 0
    }]);
  };
  const removeLineItem = id => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };
  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        // Recalculate total if quantity or unit price changes
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };
  const validateForm = () => {
    const newErrors = {};
    // Validate required fields
    if (!formData.supplier) newErrors.supplier = 'Supplier name is required';
    if (!formData.invoiceNumber) newErrors.invoiceNumber = 'Invoice number is required';
    if (!formData.invoiceDate) newErrors.invoiceDate = 'Invoice date is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.currency) newErrors.currency = 'Currency is required';
    // Validate line items
    const itemErrors = [];
    lineItems.forEach((item, index) => {
      const itemError = {};
      if (!item.description) itemError.description = 'Description is required';
      if (item.quantity <= 0) itemError.quantity = 'Quantity must be greater than 0';
      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });
    if (itemErrors.length > 0) {
      newErrors.lineItems = itemErrors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (asDraft = true) => {
    // Only validate if not a draft
    if (!asDraft && !validateForm()) {
      return;
    }
    // Prepare data for saving
    const invoiceData = {
      ...formData,
      lineItems,
      total: calculateTotal()
    };
    // Call the save handler from parent
    onSave(invoiceData, asDraft);
  };
  const toggleImageViewer = () => {
    setImageViewerOpen(!imageViewerOpen);
  };
  const toggleDock = () => {
    setIsDocked(!isDocked);
  };
  const zoomIn = () => {
    setZoomLevel(Math.min(zoomLevel + 10, 200));
  };
  const zoomOut = () => {
    setZoomLevel(Math.max(zoomLevel - 10, 50));
  };
  return <div className="mb-8" data-id="element-625">
      <div className="flex justify-between items-center mb-6" data-id="element-626">
        <div data-id="element-627">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-628">Invoice Entry</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-629">
            Enter invoice details and line items
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-630">
          <button onClick={toggleImageViewer} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-631">
            {imageViewerOpen ? <MinusIcon className="h-4 w-4 mr-1" data-id="element-632" /> : <PlusIcon className="h-4 w-4 mr-1" data-id="element-633" />}
            {imageViewerOpen ? 'Hide Image' : 'Show Image'}
          </button>
          {imageViewerOpen && <button onClick={toggleDock} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-634">
              {isDocked ? <MaximizeIcon className="h-4 w-4 mr-1" data-id="element-635" /> : <MinimizeIcon className="h-4 w-4 mr-1" data-id="element-636" />}
              {isDocked ? 'Undock' : 'Dock'}
            </button>}
        </div>
      </div>
      {/* New layout similar to DI Exception Handling */}
      <div className="grid grid-cols-2 gap-6" data-id="element-637">
        {/* Left Column - Invoice Details */}
        <div className="flex flex-col" data-id="element-638">
          {/* Invoice Header Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-639">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('header')} data-id="element-640">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-641">
                Invoice Header Information
              </h4>
              {expandedSections.header ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-642" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-643" />}
            </div>
            {expandedSections.header && <div className="px-4 py-5 sm:p-6" data-id="element-644">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-645">
                  <div className="sm:col-span-2" data-id="element-646">
                    <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700" data-id="element-647">
                      Invoice Number *
                    </label>
                    <div className="mt-1" data-id="element-648">
                      <input type="text" name="invoiceNumber" id="invoiceNumber" value={formData.invoiceNumber} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.invoiceNumber ? 'border-red-300' : ''}`} placeholder="Enter invoice number" data-id="element-649" />
                      {errors.invoiceNumber && <p className="mt-1 text-sm text-red-600" data-id="element-650">
                          {errors.invoiceNumber}
                        </p>}
                    </div>
                  </div>
                  <div data-id="element-651">
                    <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700" data-id="element-652">
                      Invoice Date *
                    </label>
                    <div className="mt-1" data-id="element-653">
                      <input type="date" name="invoiceDate" id="invoiceDate" value={formData.invoiceDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.invoiceDate ? 'border-red-300' : ''}`} data-id="element-654" />
                      {errors.invoiceDate && <p className="mt-1 text-sm text-red-600" data-id="element-655">
                          {errors.invoiceDate}
                        </p>}
                    </div>
                  </div>
                  <div data-id="element-656">
                    <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-657">
                      Due Date
                    </label>
                    <div className="mt-1" data-id="element-658">
                      <input type="date" name="dueDate" id="dueDate" value={formData.dueDate} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-659" />
                    </div>
                  </div>
                  <div data-id="element-660">
                    <label htmlFor="poReference" className="block text-sm font-medium text-gray-700" data-id="element-661">
                      PO Reference
                    </label>
                    <div className="mt-1" data-id="element-662">
                      <input type="text" name="poReference" id="poReference" value={formData.poReference} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter PO reference" data-id="element-663" />
                    </div>
                  </div>
                  <div data-id="element-664">
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-665">
                      Currency *
                    </label>
                    <div className="mt-1" data-id="element-666">
                      <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.currency ? 'border-red-300' : ''}`} data-id="element-667">
                        <option value="" data-id="element-668">Select currency</option>
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-669">
                            {currency.code} - {currency.name}
                          </option>)}
                      </select>
                      {errors.currency && <p className="mt-1 text-sm text-red-600" data-id="element-670">
                          {errors.currency}
                        </p>}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
          {/* Supplier Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-671">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('supplier')} data-id="element-672">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-673">
                Supplier Information
              </h4>
              {expandedSections.supplier ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-674" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-675" />}
            </div>
            {expandedSections.supplier && <div className="px-4 py-5 sm:p-6" data-id="element-676">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-677">
                  <div className="sm:col-span-2" data-id="element-678">
                    <label htmlFor="supplier" className="block text-sm font-medium text-gray-700" data-id="element-679">
                      Supplier Name *
                    </label>
                    <div className="mt-1" data-id="element-680">
                      <input type="text" name="supplier" id="supplier" value={formData.supplier} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.supplier ? 'border-red-300' : ''}`} placeholder="Enter supplier name" data-id="element-681" />
                      {errors.supplier && <p className="mt-1 text-sm text-red-600" data-id="element-682">
                          {errors.supplier}
                        </p>}
                    </div>
                  </div>
                  <div className="sm:col-span-2" data-id="element-683">
                    <label htmlFor="supplierID" className="block text-sm font-medium text-gray-700" data-id="element-684">
                      Supplier ID
                    </label>
                    <div className="mt-1" data-id="element-685">
                      <input type="text" name="supplierID" id="supplierID" value={formData.supplierID} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter supplier ID" data-id="element-686" />
                    </div>
                  </div>
                  <div className="sm:col-span-2" data-id="element-687">
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-688">
                      Country *
                    </label>
                    <div className="mt-1" data-id="element-689">
                      <select id="country" name="country" value={formData.country} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.country ? 'border-red-300' : ''}`} data-id="element-690">
                        <option value="" data-id="element-691">Select country</option>
                        {countries.map(country => <option key={country} value={country} data-id="element-692">
                            {country}
                          </option>)}
                      </select>
                      {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-693">
                          {errors.country}
                        </p>}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
          {/* Payment Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-694">
            <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('paymentInfo')} data-id="element-695">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-696">
                Payment Information
              </h4>
              {expandedSections.paymentInfo ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-697" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-698" />}
            </div>
            {expandedSections.paymentInfo && <div className="px-4 py-5 sm:p-6" data-id="element-699">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-700">
                  <div className="sm:col-span-2" data-id="element-701">
                    <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700" data-id="element-702">
                      Payment Terms
                    </label>
                    <div className="mt-1" data-id="element-703">
                      <select id="paymentTerms" name="paymentTerms" value={formData.paymentTerms} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-704">
                        {paymentTermsOptions.map(term => <option key={term} value={term} data-id="element-705">
                            {term}
                          </option>)}
                      </select>
                    </div>
                  </div>
                  <div className="sm:col-span-2" data-id="element-706">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-707">
                      Description
                    </label>
                    <div className="mt-1" data-id="element-708">
                      <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter invoice description" data-id="element-709" />
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Right Column - Invoice Image Viewer */}
        {imageViewerOpen && <div className={`${isDocked ? '' : 'fixed top-20 right-4 z-50 w-2/3 max-w-2xl'}`} data-id="element-710">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-711">
              <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-712">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-713">
                  Invoice Image
                </h3>
                <div className="flex space-x-2" data-id="element-714">
                  <button onClick={zoomOut} className="p-1 rounded-md hover:bg-gray-200" title="Zoom Out" data-id="element-715">
                    <MinusIcon className="h-4 w-4" data-id="element-716" />
                  </button>
                  <span className="text-sm" data-id="element-717">{zoomLevel}%</span>
                  <button onClick={zoomIn} className="p-1 rounded-md hover:bg-gray-200" title="Zoom In" data-id="element-718">
                    <PlusIcon className="h-4 w-4" data-id="element-719" />
                  </button>
                </div>
              </div>
              <div className="p-4 overflow-auto" style={{
            maxHeight: '80vh'
          }} data-id="element-720">
                <div style={{
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: 'top left'
            }} data-id="element-721">
                  <img src={invoiceImageUrl} alt="Invoice" className="w-full h-auto" data-id="element-722" />
                </div>
              </div>
            </div>
          </div>}
      </div>
      {/* Line Items (Full Width) */}
      <div className="mt-6" data-id="element-723">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-724">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200" data-id="element-725">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-726">
              Line Items
            </h3>
            <button type="button" onClick={addLineItem} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-727">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-728" />
              Add Line Item
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-729">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-730">
              <thead className="bg-gray-50" data-id="element-731">
                <tr data-id="element-732">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-733">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-734">
                    Item Code
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-735">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-736">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-737">
                    UOM
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-738">
                    Unit Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-739">
                    Tax Code
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-740">
                    GL Code
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-741">
                    Total
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-742">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-743">
                {lineItems.map(item => <tr key={item.id} data-id="element-744">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-745">
                      <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.type} onChange={e => updateLineItem(item.id, 'type', e.target.value)} data-id="element-746">
                        <option value="Item" data-id="element-747">Item</option>
                        <option value="Service" data-id="element-748">Service</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-749">
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.itemCode} onChange={e => updateLineItem(item.id, 'itemCode', e.target.value)} placeholder="Item code" data-id="element-750" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-751">
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.description} onChange={e => updateLineItem(item.id, 'description', e.target.value)} placeholder="Description" data-id="element-752" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-753">
                      <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.quantity} onChange={e => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 0)} min="0" step="1" data-id="element-754" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-755">
                      <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.uom} onChange={e => updateLineItem(item.id, 'uom', e.target.value)} data-id="element-756">
                        {uomOptions.map(uom => <option key={uom} value={uom} data-id="element-757">
                            {uom}
                          </option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-758">
                      <div className="relative rounded-md shadow-sm" data-id="element-759">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-760">
                          <span className="text-gray-500 sm:text-sm" data-id="element-761">
                            {formData.currency === 'USD' ? '$' : formData.currency}
                          </span>
                        </div>
                        <input type="text" value={item.unitPrice} onChange={e => {
                      // Remove non-numeric characters except decimal point
                      const sanitizedValue = e.target.value.replace(/[^\d.]/g, '');
                      updateLineItem(item.id, 'unitPrice', parseFloat(sanitizedValue) || 0);
                    }} className="block w-full pl-7 pr-3 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-right" placeholder="0.00" pattern="^\d*(\.\d{0,2})?$" data-id="element-762" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-763">
                      <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.taxCode} onChange={e => updateLineItem(item.id, 'taxCode', e.target.value)} data-id="element-764">
                        {taxCodes.map(code => <option key={code} value={code} data-id="element-765">
                            {code}
                          </option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-766">
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={item.glCode} onChange={e => updateLineItem(item.id, 'glCode', e.target.value)} placeholder="GL code" data-id="element-767" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-768">
                      {formatCurrency(item.quantity * item.unitPrice, formData.currency || 'USD', locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-769">
                      <button onClick={() => removeLineItem(item.id)} className="text-red-600 hover:text-red-900" data-id="element-770">
                        <TrashIcon className="h-5 w-5" data-id="element-771" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-50" data-id="element-772">
                <tr data-id="element-773">
                  <td colSpan="8" className="px-6 py-4 text-right text-sm font-medium text-gray-900" data-id="element-774">
                    Total Amount:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-775">
                    {formatCurrency(calculateTotal(), formData.currency || 'USD', locale)}
                  </td>
                  <td data-id="element-776"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
      {/* Notes Section (Full Width) - Moved below Line Items */}
      <div className="mt-6" data-id="element-777">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-778">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-779">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-780">
              Notes
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-781">
            <StandardizedNotes value={formData.notes} onChange={value => setFormData({
            ...formData,
            notes: value
          })} visibility={formData.noteVisibility} onVisibilityChange={visibility => setFormData({
            ...formData,
            noteVisibility: visibility
          })} visibilityDetails={formData.noteVisibilityDetails} onVisibilityDetailsChange={details => setFormData({
            ...formData,
            noteVisibilityDetails: details
          })} label="Invoice Notes" placeholder="Add any notes about this invoice. Select who can view these notes using the visibility options." data-id="element-782" />
          </div>
        </div>
      </div>
      {/* Action Buttons - Moved to bottom of page */}
      <div className="mt-6" data-id="element-783">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-784">
          <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-3" data-id="element-785">
            <button type="button" onClick={() => handleSubmit(true)} className="inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-786">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-787" />
              Save as Draft
            </button>
            <button type="button" onClick={() => handleSubmit(false)} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-788">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-789" />
              Submit Invoice
            </button>
          </div>
        </div>
      </div>
    </div>;
}